<script lang="ts" setup>
import { computed, inject, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import type { Ref } from 'vue';
import type { Viewer, Nullable } from '@immersive-tech/viewer-engine';
import type { IManifest, IManifestPart } from '@/types/IManifest';

const manifest = inject('manifest') as Ref<IManifest>;
const viewer = inject('viewer') as Ref<Nullable<Viewer>>;
const glbStorage = inject('glbStorage') as Ref<Map<string, string>>;
const renderIFrame = ref<Nullable<HTMLIFrameElement>>(null);
const router = useRouter();

interface IExtendedFrameWindow extends Window {
  viewer?: Viewer;
}

const createBlobURI = (content: unknown, type?: string) => {
  const blob = new Blob([content as BlobPart], { type });

  return URL.createObjectURL(blob);
};

const generatePreviewManifest = () => {
  const modifiableManifest = JSON.parse(JSON.stringify(manifest.value)) as IManifest;

  modifiableManifest.basePath = '';

  const newParts: IManifestPart[] = [];

  for (const part of modifiableManifest.parts) {
    if (!glbStorage.value.has(part.name)) continue;

    part.path = glbStorage.value.get(part.name)!;

    newParts.push(part);
  }

  modifiableManifest.parts = newParts;

  return modifiableManifest;
};

const shouldEscape = computed(() => !viewer.value);

const renderIFrameWatcher = (newValue: Nullable<HTMLIFrameElement>) => {
  if (!newValue) return;
  if (!newValue.contentWindow) return;

  const viewerFrame = newValue.contentWindow;
  const canvas = viewerFrame.document.createElement('canvas');
  const previewManifest = generatePreviewManifest();
  const manifestBlob = createBlobURI(JSON.stringify(previewManifest), 'application/json');
  const viewerBundleVersion = 'latest';

  canvas.id = 'renderCanvas';
  canvas.setAttribute('manifestPath', manifestBlob);

  viewerFrame.document.write(`
    <html>
      <head></head>
      <body>
        <div style="display: flex;flex-direction: row;width: 100%; height: 100%;flex-wrap: wrap;">
          <div id="renderCanvasContainer" style="display: flex;flex-direction: row;">
            ${canvas.outerHTML}
          </div>
        </div>
      </body>
    </html>
  `);

  const newiFrameScript = viewerFrame.document.createElement('script');
  const newiFrameStyle = viewerFrame.document.createElement('link');

  newiFrameStyle.rel = 'stylesheet';
  newiFrameStyle.href = `/viewer_style.${import.meta.env.VITE_VIEWER_VERSION}.css`;

  newiFrameScript.src = `/viewer_bundle.${import.meta.env.VITE_VIEWER_VERSION}.js`;
  newiFrameScript.type = 'module';
  newiFrameScript.onload = () => {
    const tourViewer = (viewerFrame.window as IExtendedFrameWindow).viewer;

    tourViewer?.addEventListener('MENU_ADDED', () => {
      const menuLogo = viewerFrame.document.querySelector('.menuLogo');
      const menuClose = viewerFrame.document.querySelector('.menuClose');
      const menuLogoClone = menuLogo?.cloneNode() as Nullable<HTMLElement>;
      let menuCloseClone: HTMLElement | null = null;

      if (menuClose) {
        menuCloseClone = menuClose.cloneNode() as HTMLElement;
      }

      menuCloseClone?.setAttribute('title', 'Disabled for preview');
      menuLogoClone?.setAttribute('title', 'Disabled for preview');

      if (menuLogoClone) {
        menuLogo?.replaceWith(menuLogoClone);
      }

      if (menuCloseClone) {
        menuClose?.replaceWith(menuCloseClone);
      }
    });
  };

  viewerFrame.document.head.appendChild(newiFrameStyle);
  viewerFrame.document.body.appendChild(newiFrameScript);
};

const mountHandler = () => {
  if (shouldEscape.value === true) {
    router.push('/');
    return;
  }
};

watch(renderIFrame, renderIFrameWatcher);
onMounted(mountHandler);
</script>

<template>
  <div>
    <iframe ref="renderIFrame" class="w-full bg-white" sandbox="allow-scripts allow-same-origin allow-popups" />
  </div>
</template>
